import React, { useState, useRef, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Photo } from '../types';
import { deletePhoto, getPhotosByProject, markOfflinePhotoAsDeleted } from '../services/photoService';
import PhotoViewer from './PhotoViewer';
import AiLensBadge from './AiLensBadge';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { hapticSelection } from '../utils/haptics';
import ConfirmDialog from './ConfirmDialog';

// Neutral camera-off placeholder for photos with a missing or failed URL.
// Used both up-front (an empty src never fires onError) and on a genuine
// load failure (404/401/network).
const PHOTO_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 24 24' fill='none' stroke='%239e9589' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><rect x='3' y='5' width='18' height='14' rx='2'/><circle cx='12' cy='12' r='3'/><line x1='3' y1='3' x2='21' y2='21'/></svg>"
  );

// Extended Photo interface with rotation
interface ExtendedPhoto extends Photo {
  rotation?: number;
}

interface PhotoListProps {
  photos: ExtendedPhoto[];
  projectId: string;
  onPhotoDeleted?: (photoId: string) => void;
  onAddPhotoClick?: () => void;
  showAddButton?: boolean;
  compact?: boolean;
  setPhotos?: (photos: ExtendedPhoto[]) => void;
  onSelectionModeChange?: (active: boolean) => void;
  contextLabel?: string;
}

const PhotoList: React.FC<PhotoListProps> = ({
  photos,
  projectId,
  onPhotoDeleted,
  onAddPhotoClick,
  showAddButton = true,
  compact = false,
  setPhotos,
  onSelectionModeChange,
  contextLabel
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState<ExtendedPhoto | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const isOnline = useOnlineStatus();

  // Touch discrimination refs — prevent scroll from triggering tap
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const scrolledRef = useRef(false);

  const toggleSelectionMode = useCallback(() => {
    const newMode = !selectionMode;
    setSelectionMode(newMode);
    setSelectedPhotos(new Set());
    onSelectionModeChange?.(newMode);
    if (newMode) hapticSelection();
  }, [selectionMode, onSelectionModeChange]);

  const handleViewPhoto = (photo: ExtendedPhoto) => {
    if (selectionMode) {
      togglePhotoSelection(photo.id);
    } else {
      setSelectedPhoto(photo);
    }
  };

  const togglePhotoSelection = (photoId: string) => {
    const newSelected = new Set(selectedPhotos);
    if (newSelected.has(photoId)) {
      newSelected.delete(photoId);
    } else {
      newSelected.add(photoId);
    }
    setSelectedPhotos(newSelected);
    hapticSelection();
  };

  const handleDeleteSelected = async () => {
    if (selectedPhotos.size === 0) return;

    try {
      setDeleteLoading('batch');

      for (const photoId of selectedPhotos) {
        if (!isOnline) {
          markOfflinePhotoAsDeleted(photoId);
        }
        await deletePhoto(photoId, projectId);
        if (onPhotoDeleted) {
          onPhotoDeleted(photoId);
        }
      }

      setSelectedPhotos(new Set());
      setSelectionMode(false);
      onSelectionModeChange?.(false);
    } catch (err) {
      console.error('Error deleting photos:', err);
      setError('Kunde inte ta bort alla foton. Försök igen senare.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setDeleteLoading(null);
    }
  };

  // Touch handlers for tap-vs-scroll discrimination
  const handleThumbnailTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    scrolledRef.current = false;
  }, []);

  const handleThumbnailTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - touchStartRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartRef.current.y);
    // 6px slop: a deliberate tap barely moves, so treat even small drags
    // (the old 8-9px gap slipped through at the 10px threshold and opened a
    // photo on an intended scroll) as scrolling. (#41)
    if (dx > 6 || dy > 6) {
      scrolledRef.current = true;
    }
  }, []);

  const handleThumbnailTouchEnd = useCallback((photo: ExtendedPhoto) => {
    if (scrolledRef.current || !touchStartRef.current) {
      touchStartRef.current = null;
      return;
    }
    const elapsed = Date.now() - touchStartRef.current.time;
    touchStartRef.current = null;
    if (elapsed < 400) {
      handleViewPhoto(photo);
    }
  }, [selectionMode, selectedPhotos]);

  if (selectedPhoto) {
    const selectedPhotoIndex = photos.findIndex(p => p.id === selectedPhoto.id);
    return (
      <PhotoViewer
        photos={photos}
        initialPhotoIndex={selectedPhotoIndex >= 0 ? selectedPhotoIndex : 0}
        contextLabel={contextLabel}
        onClose={() => {
          setSelectedPhoto(null);
          if (setPhotos) {
            getPhotosByProject(projectId).then(fresh => setPhotos(fresh as ExtendedPhoto[])).catch(() => {});
          }
        }}
        onDelete={onPhotoDeleted ? async (photoId) => {
          try {
            if (!isOnline) {
              markOfflinePhotoAsDeleted(photoId);
            }
            await deletePhoto(photoId, projectId);
          } catch (err) {
            console.error('Error deleting photo:', err);
          }
          onPhotoDeleted(photoId);
          setSelectedPhoto(null);
        } : undefined}
      />
    );
  }

  return (
    <div>
      {/* Error Display */}
      {error && (
        <div className="mx-3 mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-300 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Minimal Header — only shows when there are actions */}
      {(showAddButton || photos.length > 0) && (
        <div className="flex items-center justify-end px-3 py-2 gap-2">
          {showAddButton && (
            <button
              onClick={onAddPhotoClick}
              className="text-[#007AFF] text-sm font-normal flex items-center gap-1 py-1.5 px-2 active:opacity-60 transition-opacity"
            >
              <Plus size={16} />
              <span>Lägg till</span>
            </button>
          )}
          {photos.length > 0 && (
            <button
              onClick={toggleSelectionMode}
              className="text-[#007AFF] text-sm font-normal py-1.5 px-2 active:opacity-60 transition-opacity"
            >
              {selectionMode ? 'Avbryt' : 'Välj'}
            </button>
          )}
        </div>
      )}

      {/* Photo Grid — edge-to-edge, iOS Photos style */}
      {photos.length === 0 ? (
        <div className="text-center py-16 text-[var(--text-tertiary)]">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--bg-surface-alt)] flex items-center justify-center">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--text-tertiary)]">
              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>
            </svg>
          </div>
          <p className="text-base font-medium text-[var(--text-secondary)]">Inga foton ännu</p>
          <p className="text-sm mt-1">Lägg till ditt första foto</p>
        </div>
      ) : (
        <div className={`grid gap-[2px] ${
          compact
            ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5'
            : 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7'
        }`}>
          {photos.map((photo) => (
            <div
              key={photo.id}
              className={`relative aspect-square overflow-hidden cursor-pointer transition-[filter] duration-150 ${
                selectionMode && selectedPhotos.has(photo.id)
                  ? 'brightness-[0.88]'
                  : ''
              }`}
              // Desktop: use onClick
              onClick={() => handleViewPhoto(photo)}
              // Mobile: touch discrimination to prevent scroll-triggered taps
              onTouchStart={handleThumbnailTouchStart}
              onTouchMove={handleThumbnailTouchMove}
              onTouchEnd={(e) => {
                // Prevent onClick from also firing on touch devices
                if (!scrolledRef.current && touchStartRef.current) {
                  e.preventDefault();
                }
                handleThumbnailTouchEnd(photo);
              }}
            >
              <img
                src={(photo.thumbnail || photo.url)?.trim() ? (photo.thumbnail || photo.url) : PHOTO_PLACEHOLDER}
                alt="Foto"
                className={
                  (photo.thumbnail || photo.url)?.trim()
                    ? 'w-full h-full object-cover bg-[var(--bg-surface-alt)]'
                    : 'w-full h-full object-contain p-6 bg-[var(--bg-surface-alt)]'
                }
                loading="lazy"
                draggable={false}
                onError={(e) => {
                  const img = e.currentTarget;
                  if (img.dataset.fallback === '1') return;
                  img.dataset.fallback = '1';
                  img.src = PHOTO_PLACEHOLDER;
                  img.className = 'w-full h-full object-contain p-6 bg-[var(--bg-surface-alt)]';
                }}
              />

              {/* AI Lens Badge — bottom-left, hidden in selection mode */}
              {!selectionMode && <AiLensBadge aiLens={photo.aiLens} />}

              {/* iOS-style selection circle — bottom-right */}
              {selectionMode && (
                <div
                  className="absolute bottom-[6px] right-[6px] w-[24px] h-[24px] rounded-full flex items-center justify-center"
                  style={{ boxShadow: '0 0 4px rgba(0,0,0,0.3), 0 1px 3px rgba(0,0,0,0.2)' }}
                >
                  {selectedPhotos.has(photo.id) ? (
                    <div className="w-full h-full rounded-full bg-[#007AFF] flex items-center justify-center animate-selection-pop">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 6.5L5 9L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  ) : (
                    <div className="w-full h-full rounded-full bg-white/30 border-[1.5px] border-white/90 backdrop-blur-[2px]" />
                  )}
                </div>
              )}

              {/* Loading Overlay */}
              {deleteLoading === photo.id && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Fixed Bottom Toolbar — Selection Mode */}
      {selectionMode && (
        <div
          className="fixed left-0 right-0 lg:left-60 bg-[var(--bg-surface)]/95 backdrop-blur-sm border-t border-[var(--border-default)] animate-sheet-up z-50"
          style={{ bottom: 'calc(49px + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="flex items-center justify-between px-4 py-3 max-w-5xl mx-auto">
            <span className="text-sm text-[var(--text-secondary)]">
              {selectedPhotos.size > 0
                ? `${selectedPhotos.size} foto${selectedPhotos.size !== 1 ? 'n' : ''} vald${selectedPhotos.size !== 1 ? 'a' : 't'}`
                : 'Välj foton'
              }
            </span>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={selectedPhotos.size === 0 || deleteLoading === 'batch'}
              className="text-red-500 disabled:text-[var(--text-tertiary)] text-sm font-medium flex items-center gap-1.5 px-3 py-2 rounded-lg active:bg-red-50 dark:active:bg-red-900/20 transition-colors disabled:pointer-events-none"
            >
              {deleteLoading === 'batch' ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-500 border-t-transparent" />
              ) : (
                <Trash2 size={16} />
              )}
              Ta bort
            </button>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={`Ta bort ${selectedPhotos.size} foto${selectedPhotos.size !== 1 ? 'n' : ''}?`}
        message="Är du säker? Detta går inte att ångra."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        loading={deleteLoading === 'batch'}
        onConfirm={async () => {
          await handleDeleteSelected();
          setShowDeleteConfirm(false);
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default PhotoList;